import {useTranslation} from '@aboutyou/i18n';
import {getTabIndex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {PropsWithChildren, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {AnimatedWishButton, useAnimatedWishButton} from './AnimatedWishButton';

type ConfigProps = {
  variant: 'filled' | 'not-filled' | 'not-filled-with-outline';
  size?: 'normal' | 'large';
  withBackground?: boolean;
  isFocusable?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  style?: React.CSSProperties;
  backgroundColor?: 'primary' | 'secondary' | 'transparent';
};

export type WishButtonProps = PropsWithChildren<ConfigProps>;

export const WishButton: React.FC<WishButtonProps> = ({
  size,
  variant = 'not-filled-with-outline',
  onClick,
  withBackground = false,
  children,
  isFocusable,
  backgroundColor = 'transparent',
  ...restProps
}) => {
  const {t} = useTranslation();
  const [hovered, setIsHovered] = useState(false);
  const {hasClicked, resetClick, handleHasClicked} = useAnimatedWishButton();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    onClick?.(event);
    handleHasClicked();
  };

  return (
    <StyledButton
      data-testid={
        variant === 'filled' ? 'wishListButtonFilled' : 'wishListButton'
      }
      {...restProps}
      data-button-type={backgroundColor}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={
        variant === 'filled'
          ? t('Item in whishlist')
          : t('Add item to whishlist')
      }
      aria-pressed={variant === 'filled'}
      tabIndex={getTabIndex(isFocusable)}
    >
      <AnimatedWishButton
        variant={variant}
        hovered={hovered}
        hasClicked={hasClicked}
        resetClick={resetClick}
        withBackground={withBackground}
        size={size}
      />
      {children}
    </StyledButton>
  );
};

const StyledButton = styled(UnstyledButton)<Omit<WishButtonProps, 'variant'>>`
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0;
  margin: 0;
  color: ${COLORS.Black};

  &[data-button-type='primary'] {
    background-color: ${COLORS['Fill.Black']};
  }

  &[data-button-type='secondary'] {
    background-color: ${COLORS['Fill.White']};
  }

  &[data-button-type='transparent'] {
    background-color: transparent;
  }
`;
